"use strict";
/**
 * KST 달력일 ↔ 시각 변환 — **서버와 화면이 같은 함수를 쓴다.**
 *
 * 왜 shared에 있나: 설치일을 사람이 고치게 열면서(2026-09-12) 같은 규칙이 두 곳에서 필요해졌다.
 * 서버는 "보낸 날짜를 저장값으로 바꾸고, 저장값이 그 날짜와 같은지" 판정하고, 화면은 "저장값을
 * 폼과 표에 어느 날로 그릴지" 정한다. **두 벌로 두면 한쪽만 바뀐다** — 이 레포에서 같은 사고가
 * 세 번 났다(VAN사명 저장소 두 벌 2026-09-10 · 메뉴 정렬 기준 세 벌 09-04 · 그룹 판정 술어
 * 세 벌 09-04). 주석으로 서로를 가리키게 하는 것은 두 벌인 사실을 관리하는 것이지 없애는 것이
 * 아니라서, 정의를 한 벌만 둔다.
 *
 * 왜 `Intl.DateTimeFormat(..., {timeZone:'Asia/Seoul'})`이 아닌가: KST는 서머타임이 없어
 * 결과는 같지만, Intl은 **런타임 tzdata와 로케일 출력 형식** 두 가지에 더 기댄다. 브라우저와
 * 서버가 같은 답을 내는 것이 이 파일의 목적이므로 산술로 고정한다.
 *
 * ⚠ 화면의 `formatDate`(브라우저 로컬)와는 다른 함수다. 설치일처럼 **서버 판정과 화면 표시가
 * 같은 날을 말해야 하는 값**만 이쪽을 쓴다 — 전부 옮기면 출고일·등록일 등 33곳이 이번 목표와
 * 무관하게 따라 움직인다.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.kstDateToDate = kstDateToDate;
exports.dateToKstDate = dateToKstDate;
exports.dateToKstDateTime = dateToKstDateTime;
exports.dateToKstDateTimeSec = dateToKstDateTimeSec;
exports.isoToKstDate = isoToKstDate;
/** KST 오프셋 — 서머타임이 없어 고정값이다 */
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
/*
 * ⚠ **무효 입력에 대한 태도가 셋이 같지 않다 — 의도된 비대칭이다.**
 *
 * `dateToKstDate`·`dateToKstDateTime`은 `Date`를 받아 `toISOString()`을 지나므로 `new Date(NaN)`이
 * 들어오면 **`RangeError`를 던진다.** 반면 `isoToKstDate`는 화면 입구라 파싱 실패를 `null`로 삼킨다
 * (표 한 칸 때문에 목록이 통째로 안 그려지면 안 된다).
 *
 * 지금 서버 호출부는 전부 pg 드라이버가 준 `Date`라 무효 값이 도달하지 않는다. 그래서 던지는 쪽을
 * 고치지 않았다 — 조용히 `null`로 흘리면 "엑셀에 빈 칸"이 되어 원인을 못 찾는다.
 *
 * **앞의 둘을 파싱 결과에 직접 물리지 마라.** `dateToKstDate(new Date(사용자입력))`처럼 쓰면
 * 값 하나가 깨졌을 때 **목록 API가 통째로 500**이 된다. 문자열에서 출발하는 값은 `isoToKstDate`를
 * 쓰거나, 호출부에서 유효성을 먼저 가른 뒤 넘긴다.
 */
/** KST 달력일(YYYY-MM-DD) → 그날 00:00 KST 시각 (timestamptz 저장용) */
function kstDateToDate(value) {
    return new Date(`${value}T00:00:00+09:00`);
}
/**
 * 시각 → 그 시각의 KST 달력일 YYYY-MM-DD (`kstDateToDate`의 역방향).
 *
 * "사람이 보는 날짜"를 뽑는다. 저장된 timestamptz와 사람이 보낸 날짜 문자열을 견줄 때,
 * 그리고 화면이 그 값을 그릴 때 쓴다 — **시각까지 견주면 같은 날을 되보낸 요청이 변경으로
 * 잡혀**, 설치일을 건드리지 않은 사용자까지 권한 검사에 걸린다.
 */
function dateToKstDate(value) {
    return new Date(value.getTime() + KST_OFFSET_MS).toISOString().slice(0, 10);
}
/**
 * 시각 → KST `YYYY-MM-DD HH:mm`.
 *
 * 엑셀에 **시각을 담은 값**을 내보낼 때 쓴다. `Date`를 그대로 실으면 안 되는 이유가 두 겹이다:
 * ExcelJS는 스타일 없는 `Date` 셀에 내장 서식 `mm-dd-yy`를 붙이므로 **시각이 아예 안 보이고**
 * (`xlsx/xform/style/styles-xform.js:261`), 시리얼은 UTC 기준이라
 * (`utils/utils.js:56`) KST 00:00~08:59 값이 **하루 이르게** 표시된다. 둘이 겹치면
 * "9/12 08:00에 수신"이 엑셀에서 `09-11-26`이 된다.
 *
 * 형식은 화면의 `formatDateTime`과 같은 글자를 목표로 한다 — 같은 값을 표와 엑셀이 다르게
 * 보여 주면 안 된다. ⚠ 다만 화면 쪽은 **브라우저 로컬** 기준이라 비KST 브라우저에서는 갈린다
 * (그쪽을 KST로 옮기는 것은 별개 판단이다).
 */
function dateToKstDateTime(value) {
    const kst = new Date(value.getTime() + KST_OFFSET_MS);
    const hh = String(kst.getUTCHours()).padStart(2, '0');
    const mm = String(kst.getUTCMinutes()).padStart(2, '0');
    return `${dateToKstDate(value)} ${hh}:${mm}`;
}
/**
 * 시각 → KST `YYYY-MM-DD HH:mm:ss` — **초까지 필요한 값 전용**.
 *
 * 통신 로그가 그렇다. 전문 왕복 한 번에 IN·OUT 두 행이 **같은 초 안에** 쌓이므로, 분 단위로
 * 내보내면 엑셀에서 순서도 간격도 읽히지 않는다. 로그를 내려받아 분석하는 것이 이 화면의
 * 목적(이성진 소장 요구)이라 그 자리에서는 초가 값이다.
 *
 * ⚠ 이것을 기본으로 삼지 말 것. 등록일·최종상태수신처럼 사람이 읽는 시각은
 * `dateToKstDateTime`(분)이 화면 `formatDateTime`과 같은 글자를 낸다 — 섞으면 같은 값이
 * 화면과 엑셀에서 다르게 보인다.
 */
function dateToKstDateTimeSec(value) {
    const kst = new Date(value.getTime() + KST_OFFSET_MS);
    const ss = String(kst.getUTCSeconds()).padStart(2, '0');
    return `${dateToKstDateTime(value)}:${ss}`;
}
/**
 * ISO 문자열 → KST 달력일. 값이 없거나 읽을 수 없으면 `null`.
 *
 * 화면용 입구다 — API가 주는 것은 ISO 문자열이고 `Date`가 아니다. 파싱 실패를 `null`로
 * 흘려보내는 이유: 여기서 던지면 표 한 칸 때문에 목록 전체가 안 그려진다.
 */
function isoToKstDate(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : dateToKstDate(parsed);
}
